import { ConsentRecord, StoreDoc, UserDoc, UserRole, storeCategories } from '@gooduncles/gu-app-schema';
import { Button, message } from 'antd';
import type { Timestamp } from 'firebase/firestore';
import { FC, useCallback } from 'react';
import { storeStateList, userRoleList, userStatusKr } from 'src/constants';
import { useAuth } from 'src/stores/auth-context';

import { getDataDogLinkForUser } from 'src/utils/common';
import { formatDate } from 'src/utils/date-util';
import { getStore } from 'src/utils/firebase/firebase-short-cut';

import ServiceVersionTag from 'src/atomic-components/atoms/ServiceVersionTag';
import AddProductToListModal from 'src/atomic-components/organisms/Modals/AddProductToListModal';
import ToggleEditor from 'src/components/Common/ToggleEditor/ToggleEditor';

import classes from './UserInfo.module.scss';

type UserInfoProps = {
  userWithStore: UserDoc & {
    store: StoreDoc | null;
  };
  mutateUser: (field: any, value: any) => Promise<void>;
  mutateStore: (field: any, value: any) => Promise<void>;
};

type FlexRowProps = {
  label: string;
  value: string | boolean | null | undefined;
  field: string;
  link?: string;
};

const FlexRow: FC<FlexRowProps> = ({ label, value, field, link }) => (
  <div className={classes.flexRow}>
    <div className={classes.label}>
      {label}
      {field && <span className={classes.subLabel}>({field})</span>}
    </div>
    {link && value ? (
      <a href={link} target='_blank' rel='noopener noreferrer' className={classes.value}>
        {value}
      </a>
    ) : (
      <p className={classes.value}>{value ?? '-'}</p>
    )}
  </div>
);

const formatTimestamp = (timestamp: Timestamp | undefined): string | undefined =>
  timestamp ? formatDate(timestamp.toDate(), 'yyyy-MM-dd HH:mm:ss') : undefined;

type ConsentRowProps = {
  label: string;
  record: ConsentRecord | undefined;
};

const ConsentRow: FC<ConsentRowProps> = ({ label, record }) => {
  const valueText = record
    ? `${record.agreed ? '✅' : '❌'} · ${formatDate(record.agreedAt, 'yyyy-MM-dd HH:mm:ss')} · v${record.version}`
    : '-';
  return (
    <div className={classes.flexRow}>
      <div className={classes.label}>{label}</div>
      <p className={classes.value}>{valueText}</p>
    </div>
  );
};

/**
 * @param targetRole 현재 대상의 역할
 * @param managerRole 변경하려는 관리자의 역할
 * @returns
 */
const roleValidator = (targetRole: string, managerRole: UserRole) => {
  if (['admin', 'manager'].includes(managerRole)) {
    // 관리자 끼리는 변경할 수 있으나 manager는 관리자의 역할을 변경할 수 없다.
    if (managerRole === 'manager' && targetRole === 'admin') {
      return false;
    }
    // 그 외의 역할은 admin 또는 manager만 변경할 수 있다.
    return true;
  }
  return false;
};

const UserInfo: FC<UserInfoProps> = ({ userWithStore, mutateUser, mutateStore }) => {
  const { user } = useAuth();
  const isAuthorized = roleValidator(userWithStore.role, user?.role ?? 'unknown');

  const validateStoreId = useCallback(async (value: string) => {
    try {
      const isStoreExist = await getStore(value);
      if (!isStoreExist) {
        message.error('존재하지 않는 매장 id입니다.');
        return false;
      }
      return true;
    } catch (error) {
      console.error(error);
      message.error('매장 id 검증에 실패했습니다.');
      return false;
    }
  }, []);

  const isV2 = userWithStore.serviceVersion === 2;

  return (
    <div className={classes.userInfoContainer}>
      {/* UserInfo */}
      <div className={classes.flexColumn}>
        <h2>
          사용자 정보
          <ServiceVersionTag serviceVersion={userWithStore.serviceVersion} style={{ marginLeft: 8 }} />
          <Button
            type='link'
            onClick={() =>
              userWithStore.email ? getDataDogLinkForUser(userWithStore.email, userWithStore.serviceVersion) : undefined
            }
          >
            Datadog Link
          </Button>
        </h2>
        <FlexRow label='식별자' value={userWithStore._id} field='_id' />
        <FlexRow label='이메일' value={userWithStore.email} field='email' />
        <ToggleEditor
          label='사용자 이름'
          value={userWithStore.userName ?? ''}
          field='userName'
          type='string'
          handleOnEdit={mutateUser}
          isAuthorized={isAuthorized}
        />
        <ToggleEditor
          label='계정상태'
          value={userWithStore.status}
          type='select'
          field='status'
          isAuthorized={isAuthorized}
          handleOnEdit={mutateUser}
          options={Object.entries(userStatusKr).map(([value, label]) => ({ value, label }))}
        />
        <ToggleEditor
          label='역할'
          value={userWithStore.role}
          type='select'
          field='role'
          isAuthorized={isAuthorized}
          handleOnEdit={mutateUser}
          options={userRoleList.map((role: string) => ({ label: role, value: role }))}
        />
        <ToggleEditor
          label='전화번호'
          value={userWithStore.userTel}
          field='userTel'
          type='string'
          handleOnEdit={mutateUser}
          isAuthorized={isAuthorized}
        />
        <ToggleEditor
          label='매장 id'
          value={userWithStore.storeId}
          field='storeId'
          type='string'
          validation={validateStoreId}
          handleOnEdit={mutateUser}
          isAuthorized={isAuthorized}
        />
        <div className={classes.flexRow}>
          <div className={classes.label}>
            즐겨찾기<span className={classes.subLabel}>(favoriteProducts)</span>
          </div>
          <div className={classes.value} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{userWithStore.favoriteProducts?.length ?? 0}개</span>
            {isAuthorized && (
              <AddProductToListModal
                mode='staged'
                title='즐겨찾기 편집'
                showCurrentList
                enableReorder
                currentProductIds={userWithStore.favoriteProducts ?? []}
                onSave={(ids) => mutateUser('favoriteProducts', ids)}
                openButton={<Button size='small'>편집</Button>}
              />
            )}
          </div>
        </div>
        <FlexRow label='카카오 연동 ID' value={userWithStore.kakaoUserId ?? undefined} field='kakaoUserId' />

        {isV2 ? (
          <>
            <ToggleEditor
              label='일일 가격 알림'
              value={(!!userWithStore.subscribeDailyPrice).toString()}
              field='subscribeDailyPrice'
              type='boolean'
              handleOnEdit={mutateUser}
              isAuthorized={isAuthorized}
            />
            <ToggleEditor
              label='주문 마감시간 알림'
              value={(!!userWithStore.subscribeDeadline).toString()}
              field='subscribeDeadline'
              type='boolean'
              handleOnEdit={mutateUser}
              isAuthorized={isAuthorized}
            />
          </>
        ) : (
          <>
            <ToggleEditor
              label='주문 마감시간 알림'
              value={(!!userWithStore.deadlineNotification).toString()}
              field='deadlineNotification'
              type='boolean'
              handleOnEdit={mutateUser}
              isAuthorized={isAuthorized}
            />
            <ToggleEditor
              label='일일 카톡 알림'
              value={(!!userWithStore.kakaoNotification).toString()}
              field='kakaoNotification'
              type='boolean'
              handleOnEdit={mutateUser}
              isAuthorized={isAuthorized}
            />
            <ToggleEditor
              label='새벽 알림 금지'
              value={(!!userWithStore.disableNotificationAtDawn).toString()}
              field='disableNotificationAtDawn'
              type='boolean'
              handleOnEdit={mutateUser}
              isAuthorized={isAuthorized}
            />
          </>
        )}

        {isV2 && (
          <FlexRow label='전화 인증 연동' value={userWithStore.phoneLinked ? '연동됨' : '미연동'} field='phoneLinked' />
        )}

        {isV2 && userWithStore.agreements && (
          <>
            <h3 className={classes.subSectionTitle}>약관 동의</h3>
            <ConsentRow label='이용약관 (필수)' record={userWithStore.agreements.terms} />
            <ConsentRow label='개인정보 (필수)' record={userWithStore.agreements.privacy} />
            <ConsentRow label='마케팅 (선택)' record={userWithStore.agreements.marketing} />
          </>
        )}

        <FlexRow label='기기 고유 번호 *앱' value={userWithStore.deviceTokenId} field='deviceTokenId' />
        <FlexRow label='마지막 접속 세션' value={userWithStore.sessionId} field='sessionId' />
        <FlexRow label='마지막 접속 ip' value={userWithStore.ip} field='ip' />
        <FlexRow label='브라우저' value={userWithStore.userAgent?.browser?.name} field='userAgent.browser' />
        <FlexRow label='OS' value={userWithStore.userAgent?.os?.name} field='userAgent.os' />
        <FlexRow label='빌드' value={userWithStore.version} field='version' />
        <FlexRow
          label='구독 채널'
          value={`${userWithStore.subscribedChannelIds?.length ?? 0}개`}
          field='subscribedChannelIds'
        />
        <FlexRow label='생성일' value={formatTimestamp(userWithStore._timeCreate)} field='_timeCreate' />
        <FlexRow label='업데이트일' value={formatTimestamp(userWithStore._timeUpdate)} field='_timeUpdate' />
        {userWithStore._timeDelete && (
          <FlexRow label='삭제일' value={formatTimestamp(userWithStore._timeDelete)} field='_timeDelete' />
        )}
      </div>

      {/* StoreInfo */}
      <div className={classes.flexColumn}>
        <h2>매장 정보</h2>
        {userWithStore.store ? (
          <>
            <FlexRow label='매장 식별자' field='storeId' value={userWithStore.store._id} />
            <FlexRow label='매장코드' field='storeCode' value={userWithStore.store.storeCode} />
            <ToggleEditor
              label='매장상태'
              value={storeStateList[userWithStore.store.state]}
              field='state'
              type='select'
              options={Object.entries(storeStateList).map(([value, label]) => ({ label, value }))}
              handleOnEdit={mutateStore}
              isAuthorized={isAuthorized}
            />
            <ToggleEditor
              label='매장명 *사용자 등록'
              value={userWithStore.store.storeName}
              field='storeName'
              type='string'
              handleOnEdit={mutateStore}
              isAuthorized={isAuthorized}
            />
            <ToggleEditor
              label='매장명 *관리자 등록'
              value={userWithStore.store.storeNickname}
              field='storeNickname'
              type='string'
              handleOnEdit={mutateStore}
              isAuthorized={isAuthorized}
            />
            <FlexRow
              label='업종'
              field='categories'
              value={userWithStore.store.categories.map((c) => storeCategories[c]).join(', ')}
            />
            <FlexRow label='주소(사용자 선택)' field='address' value={userWithStore.store.address} />
            <FlexRow label='지번 주소' field='jibunAddress' value={userWithStore.store.jibunAddress} />
            <FlexRow label='도로명 주소' field='roadAddress' value={userWithStore.store.roadAddress} />
            <FlexRow label='상세주소' field='addressDetail' value={userWithStore.store.addressDetail} />
            <FlexRow
              label='배송지(노션)'
              field='locationInfoUrl'
              value={userWithStore.store.locationInfoUrl}
              link={userWithStore.store.locationInfoUrl}
            />
            <ToggleEditor
              label='관리자 전화번호'
              value={userWithStore.store.ownerTel ?? ''}
              field='ownerTel'
              type='string'
              handleOnEdit={mutateStore}
              isAuthorized={isAuthorized}
            />
            <ToggleEditor
              label='맞춤상품'
              value={userWithStore.store.unhiddenProducts?.join(', ') ?? ''}
              field='unhiddenProducts'
              type='string'
              handleOnEdit={mutateStore}
              isAuthorized={isAuthorized}
            />
            <ToggleEditor
              label='상호명'
              value={userWithStore.store.businessName ?? ''}
              field='businessName'
              type='string'
              handleOnEdit={mutateStore}
              isAuthorized={isAuthorized}
            />
            <ToggleEditor
              label='사업자번호'
              value={userWithStore.store.businessNumber ?? ''}
              field='businessNumber'
              type='string'
              handleOnEdit={mutateStore}
              isAuthorized={isAuthorized}
            />
            <ToggleEditor
              label='사업자주소'
              value={userWithStore.store.businessAddress ?? ''}
              field='businessAddress'
              type='string'
              handleOnEdit={mutateStore}
              isAuthorized={isAuthorized}
            />
            <ToggleEditor
              label='대표'
              value={userWithStore.store.ownerName ?? ''}
              field='ownerName'
              type='string'
              handleOnEdit={mutateStore}
              isAuthorized={isAuthorized}
            />
            <ToggleEditor
              label='대표생년월일'
              value={userWithStore.store.ownerBirthday ?? ''}
              field='ownerBirthday'
              type='string'
              handleOnEdit={mutateStore}
              isAuthorized={isAuthorized}
            />
            <FlexRow label='사업자등록상태' value={userWithStore.store.businessStatus} field='businessStatus' />
            <FlexRow label='과세유형' value={userWithStore.store.taxType} field='taxType' />
            <FlexRow label='폐업일' value={userWithStore.store.endDate ?? undefined} field='endDate' />
          </>
        ) : (
          <div>store 정보가 없습니다.</div>
        )}
      </div>
    </div>
  );
};

export default UserInfo;
